// Excel експортът на „Сторно" — по един набор колони на изглед, за да излиза
// точно това, което човек вижда на екрана. Числата остават ЧИСЛА.

import dayjs from "dayjs";

import { exportToExcel } from "../../../../utils/excelExport";
import { parseDecimal } from "../../../../components/dataTable";
import type { CancelledItem } from "../../../../api/reportsCancellations";
import type { ProductVoidRow, WaiterVoidRow } from "./cancellationsMath";
import type { CancellationsView } from "./CancellationsTabs";

type Context = {
  t: (key: string) => string;
  waiterName: (id: number | null | undefined) => string;
  itemName: (id: number) => string;
  stationName: (id: number) => string;
};

// БЕЗ собствен времеви печат — `exportToExcel` вече лепи `_${stamp}`.
// Редовете идват ФИЛТРИРАНИ и ПОДРЕДЕНИ от таблицата (`onFilteredRowsChange`),
// не суровият отговор: файлът повтаря екрана, включително филтрите в колоните.
const fileName = (t: Context["t"]) => t("reportsPage.cancellationsTitle");

export const exportCancellations = (
  view: CancellationsView,
  rows: { list: CancelledItem[]; waiter: WaiterVoidRow[]; product: ProductVoidRow[] },
  context: Context,
) => {
  const { t, waiterName, itemName, stationName } = context;

  if (view === "waiter") {
    exportToExcel<WaiterVoidRow>({
      filename: fileName(t),
      sheetName: t("reportsPage.voidsByWaiter"),
      columns: [
        { header: t("reportsPage.waiter"), value: (r) => waiterName(r.waiterId), width: 22 },
        { header: t("reportsPage.bills"), value: (r) => r.bills, width: 10 },
        { header: t("reportsPage.voidsRows"), value: (r) => r.rows, width: 14 },
        { header: t("reportsPage.voidsAfterKitchen"), value: (r) => r.afterKitchen, width: 14 },
        { header: t("reportsPage.voidsValue"), value: (r) => r.total, width: 14 },
        {
          header: t("reportsPage.voidsShareOfRevenue"),
          value: (r) => (r.shareOfRevenue === null ? "" : Number(r.shareOfRevenue.toFixed(2))),
          width: 16,
        },
      ],
      rows: rows.waiter,
    });
    return;
  }

  if (view === "product") {
    exportToExcel<ProductVoidRow>({
      filename: fileName(t),
      sheetName: t("reportsPage.voidsByProduct"),
      columns: [
        { header: t("reportsPage.product"), value: (r) => itemName(r.itemId), width: 30 },
        { header: t("reportsPage.station"), value: (r) => stationName(r.stationId), width: 18 },
        { header: t("reportsPage.voidsRows"), value: (r) => r.rows, width: 14 },
        { header: t("reportsPage.qty"), value: (r) => r.quantity, width: 10 },
        { header: t("reportsPage.voidsAfterKitchen"), value: (r) => r.afterKitchen, width: 14 },
        { header: t("reportsPage.voidsValue"), value: (r) => r.total, width: 14 },
      ],
      rows: rows.product,
    });
    return;
  }

  exportToExcel<CancelledItem>({
    filename: fileName(t),
    sheetName: t("reportsPage.cancellations"),
    columns: [
      { header: t("reportsPage.table"), value: (r) => r.bill_name ?? "", width: 16 },
      {
        header: t("reportsPage.voidsTime"),
        value: (r) => dayjs(r.cancelled_at).format("DD.MM.YYYY HH:mm"),
        width: 18,
      },
      { header: t("reportsPage.product"), value: (r) => itemName(r.item_id), width: 28 },
      { header: t("reportsPage.comment"), value: (r) => r.reason ?? "", width: 28 },
      { header: t("reportsPage.waiter"), value: (r) => waiterName(r.waiter_id), width: 20 },
      {
        header: t("reportsPage.voidsCancelledBy"),
        value: (r) => waiterName(r.cancelled_by_user_id),
        width: 20,
      },
      { header: t("reportsPage.qty"), value: (r) => parseDecimal(r.quantity), width: 10 },
      {
        header: t("reportsPage.unitPrice"),
        value: (r) => parseDecimal(r.price),
        width: 12,
      },
      {
        header: t("reportsPage.voidsScrap"),
        value: (r) => (r.is_defective ? "✓" : ""),
        width: 10,
      },
      // Колко реда са сторнирани заедно с този — същото, което скобата показва.
      { header: t("reportsPage.voidsBatchColumn"), value: (r) => r.batch_size ?? 1, width: 12 },
      { header: t("reportsPage.amount"), value: (r) => parseDecimal(r.total_sum), width: 14 },
    ],
    rows: rows.list,
  });
};
